/**
 * Success Modal Component
 * 
 * Modal popup for successful form submission
 * Similar to screenshot: white modal with green checkmark, greeting, and success message
 *
 * @package DesignSchool\Forms
 */

declare var jQuery: any;

export interface SuccessModalOptions {
    userName?: string;
    message?: string;
    callback?: () => void;
}

export class SuccessModal {
    private $: any;
    private $overlay: any;
    private $modal: any;
    private $body: any;
    private $html: any;
    private isOpenState: boolean = false;
    private modalClass: string = 'ds-success-modal';
    private overlayClass: string = 'ds-success-modal__overlay';
    private modalOpenClass: string = 'ds-success-modal-open';

    constructor() {
        this.$ = jQuery;
        this.$body = this.$('body');
        this.$html = this.$('html');
        this.init();
    }

    /**
     * Initialize modal structure
     */
    private init(): void {
        // Create overlay
        this.$overlay = this.$('.' + this.overlayClass);
        if (!this.$overlay.length) {
            this.$overlay = this.$('<div>')
                .addClass(this.overlayClass)
                .appendTo(this.$body);
        }

        // Create modal if it doesn't exist
        this.$modal = this.$('.' + this.modalClass);
        if (!this.$modal.length) {
            this.$modal = this.$('<div>')
                .addClass(this.modalClass)
                .appendTo(this.$body);
        }

        // Bind close handlers
        this.bindCloseHandlers();
    }

    /**
     * Bind close event handlers
     */
    private bindCloseHandlers(): void {
        // Close on overlay click
        this.$overlay.on('click', (e: Event) => {
            if (e.target === this.$overlay[0]) {
                this.close();
            }
        });

        // Close when clicking outside the modal content (on the modal backdrop)
        this.$modal.on('click', (e: Event) => {
            if (e.target === this.$modal[0]) {
                this.close();
            }
        });

        // Close on ESC key
        this.$(document).on('keydown.success-modal', (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.isOpenState) {
                this.close();
            }
        });
    }

    /**
     * Show success modal
     */
    public show(options: SuccessModalOptions = {}): void {
        const userName = options.userName || '';
        const message = options.message || 'Ваша заявка принята!';

        // Build HTML
        const html = `
            <div class="ds-success-modal__container">
                <button class="ds-success-modal__close" type="button" aria-label="Закрыть">
                    <span class="ds-success-modal__close-icon">×</span>
                </button>
                
                <div class="ds-success-modal__icon">
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="32" cy="32" r="32" fill="#D4EDDA"/>
                        <path d="M20 32L28 40L44 24" stroke="#28A745" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                
                ${userName ? `<div class="ds-success-modal__greeting">Здравствуйте, ${this.escapeHtml(userName)}</div>` : ''}
                
                <div class="ds-success-modal__message">${this.escapeHtml(message)}</div>
            </div>
        `;

        // Set content
        this.$modal.html(html);

        // Bind close button
        this.$modal.find('.ds-success-modal__close').on('click', () => {
            this.close();
            if (options.callback) {
                options.callback();
            }
        });

        // Show modal
        this.$overlay.addClass('ds-success-modal__overlay--active');
        this.$modal.addClass('ds-success-modal--active');
        this.$body.addClass(this.modalOpenClass);
        this.$html.addClass(this.modalOpenClass);
        // Prevent page scroll (both html and body for cross-browser/theme compatibility)
        this.$html.css('overflow', 'hidden');
        this.$body.css('overflow', 'hidden');

        this.isOpenState = true;

        // Trigger event
        this.$(document).trigger('ds-forms-success-modal-opened', [this.$modal]);

        // Auto-close after 5 seconds
        setTimeout(() => {
            if (this.isOpenState) {
                this.close();
                if (options.callback) {
                    options.callback();
                }
            }
        }, 5000);
    }

    /**
     * Close modal
     */
    public close(): void {
        if (!this.isOpenState) {
            return;
        }

        // Hide modal
        this.$overlay.removeClass('ds-success-modal__overlay--active');
        this.$modal.removeClass('ds-success-modal--active');
        this.$body.removeClass(this.modalOpenClass);
        this.$html.removeClass(this.modalOpenClass);
        this.$html.css('overflow', '');
        this.$body.css('overflow', '');

        this.isOpenState = false;

        // Trigger event
        this.$(document).trigger('ds-forms-success-modal-closed', [this.$modal]);
    }

    /**
     * Check if modal is open
     */
    public isOpen(): boolean {
        return this.isOpenState;
    }

    /**
     * Escape HTML
     */
    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Destroy modal (cleanup)
     */
    public destroy(): void {
        this.$(document).off('keydown.success-modal');
        this.close();
    }
}

// Export singleton
export const successModal = new SuccessModal();
